import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Badge,
  Button,
  Checkbox,
  Container,
  Group,
  Loader,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconPlus, IconSearch } from '@tabler/icons-react';

import { LogoPaper, PageTitle, StatusBadge } from '@/components';
import { JURISDICTIONS } from '@/constants';
import { useNetworkPartners } from '@/hooks/useNetworkPartners';
import { formatNpLocation, NP_SCOPES, NP_STATUS_META, scopeLabel } from '@/types/network-partner';

const PAGE_SIZE = 25;

/** Archived and banned partners are hidden unless explicitly requested. */
const DEFAULT_STATUSES = ['prospect', 'active', 'suspended'];

const JURISDICTION_OPTIONS = JURISDICTIONS.map((c) => ({
  value: c.alpha2code,
  label: `${c.name} (${c.alpha2code})`,
}));

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All (not archived)' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'archived', label: 'Archived' },
  { value: 'banned', label: 'Banned' },
];

export function NetworkPartnersPage() {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 250);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [scope, setScope] = useState<string | null>(null);
  const [jurisdiction, setJurisdiction] = useState<string | null>(null);
  const [needsAttention, setNeedsAttention] = useState(false);
  const [page, setPage] = useState(1);

  const statuses = statusFilter === 'all' ? DEFAULT_STATUSES : [statusFilter];

  const { items, total, isLoading, error } = useNetworkPartners({
    statuses,
    search: debouncedSearch || undefined,
    scope: scope ?? undefined,
    jurisdiction: jurisdiction ?? undefined,
    needsAttention,
    page,
    pageSize: PAGE_SIZE,
  });

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const resetPage = () => setPage(1);

  return (
    <Stack gap="xl">
      <PageTitle
        title="Network Partners"
        subtitle="The partner roster — source of truth for scopes, coverage, and lifecycle"
      />

      <Container size="xl" px={0}>
        <LogoPaper>
          <Stack gap="md">
            <Group justify="space-between" align="flex-end" wrap="wrap">
              <Group gap="sm" align="flex-end" wrap="wrap">
                <TextInput
                  placeholder="Search name, email, company…"
                  leftSection={<IconSearch size={16} />}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.currentTarget.value);
                    resetPage();
                  }}
                  w={260}
                  data-testid="np-list-search"
                />
                <Select
                  label="Status"
                  data={STATUS_FILTER_OPTIONS}
                  value={statusFilter}
                  onChange={(v) => {
                    setStatusFilter(v ?? 'all');
                    resetPage();
                  }}
                  w={170}
                  data-testid="np-status-filter"
                />
                <Select
                  label="Scope"
                  placeholder="Any"
                  data={NP_SCOPES}
                  value={scope}
                  onChange={(v) => {
                    setScope(v);
                    resetPage();
                  }}
                  clearable
                  w={220}
                  data-testid="np-scope-filter"
                />
                <Select
                  label="Jurisdiction"
                  placeholder="Any"
                  data={JURISDICTION_OPTIONS}
                  value={jurisdiction}
                  onChange={(v) => {
                    setJurisdiction(v);
                    resetPage();
                  }}
                  searchable
                  clearable
                  w={210}
                  data-testid="np-jurisdiction-filter"
                />
                <Checkbox
                  label="Needs attention"
                  description="No scopes or no location"
                  checked={needsAttention}
                  onChange={(e) => {
                    setNeedsAttention(e.currentTarget.checked);
                    resetPage();
                  }}
                  data-testid="np-needs-attention"
                />
              </Group>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => navigate('/admin/network-partners/new')}
              >
                Add partner
              </Button>
            </Group>

            {error && (
              <Text c="red" size="sm">
                {error.message}
              </Text>
            )}

            {isLoading && items.length === 0 ? (
              <Group justify="center" py="xl">
                <Loader />
              </Group>
            ) : items.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                No partners match these filters.
              </Text>
            ) : (
              <Table.ScrollContainer minWidth={960}>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Company</Table.Th>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Location</Table.Th>
                      <Table.Th>Scopes</Table.Th>
                      <Table.Th>Backoffice</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {items.map((partner) => {
                      const statusMeta = NP_STATUS_META[partner.status] ?? {
                        label: partner.status,
                        color: 'gray',
                      };
                      return (
                        <Table.Tr
                          key={partner.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/admin/network-partners/${partner.id}`)}
                          data-testid={`np-row-${partner.id}`}
                        >
                          <Table.Td>
                            <Text fw={500}>{partner.name ?? partner.email}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{partner.company ?? '—'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c="dimmed">
                              {partner.email}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <StatusBadge color={statusMeta.color} label={statusMeta.label} />
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" lineClamp={1}>
                              {formatNpLocation(partner) ?? '—'}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" lineClamp={1} data-testid={`np-scopes-${partner.id}`}>
                              {partner.scope_keys.length > 0
                                ? partner.scope_keys.map(scopeLabel).join(', ')
                                : '—'}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            {partner.backoffice_user_id != null ? (
                              <Tooltip label={`Backoffice user #${partner.backoffice_user_id}`}>
                                <Badge size="sm" variant="light" color="green">
                                  Linked
                                </Badge>
                              </Tooltip>
                            ) : (
                              <Badge size="sm" variant="light" color="gray">
                                Not linked
                              </Badge>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}

            {totalPages > 1 && (
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of{' '}
                  {total} partners
                </Text>
                <Pagination value={page} onChange={setPage} total={totalPages} />
              </Group>
            )}
          </Stack>
        </LogoPaper>
      </Container>
    </Stack>
  );
}
